import { ExternalLink } from "lucide-react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { FivetranConnection } from "./fivetran";
import {
  getStatusBadgeVariant,
  getStatusIcon,
  getStatusLabel,
  SERVICE_DESCRIPTIONS,
  SERVICE_LABELS,
} from "./fivetran";

interface ServiceCardProps {
  service: "salesforce" | "hubspot" | "marketo" | "gongio";
  connection?: FivetranConnection;
  size?: "compact" | "full";
  showLastUpdated?: boolean;
}

export function ServiceCard({
  service,
  connection,
  size = "compact",
  showLastUpdated = false,
}: ServiceCardProps) {
  const isConnected = connection?.setup_state === "connected";

  return (
    <Card className="hover:shadow-md transition-shadow flex flex-col h-full">
      <CardHeader className="flex-grow">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {SERVICE_LABELS[service]}
            {connection && (
              <span className="flex items-center gap-2">
                {getStatusIcon(connection.setup_state)}
              </span>
            )}
          </CardTitle>
          {connection && (
            <Badge variant={getStatusBadgeVariant(connection.setup_state)}>
              {getStatusLabel(connection.setup_state)}
            </Badge>
          )}
        </div>
        <CardDescription className="mt-2">
          {SERVICE_DESCRIPTIONS[service]}
        </CardDescription>
        {connection && showLastUpdated && (
          <div className="text-xs text-muted-foreground/60 mt-2">
            Last Updated{" "}
            {new Date(connection.updated_at).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        <Link to={`/settings/connections/${service}`}>
          <Button
            className="w-full"
            size={size === "compact" ? "lg" : "default"}
            variant={isConnected ? "outline" : "default"}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {isConnected
              ? `Manage ${SERVICE_LABELS[service]}`
              : `Connect ${SERVICE_LABELS[service]}`}
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
